import { forwardRef, type Ref, type ReactNode } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { Badge } from '../../core/Badge/Badge.js';

export type EnvName = 'production' | 'staging' | 'development' | 'preview' | 'local' | (string & {});

export interface EnvironmentBadgeProps extends Omit<BaseProps, 'color' | 'variant'> {
  env: EnvName;
  /** Override the displayed label. Defaults to a Title-cased env name. */
  children?: ReactNode;
  size?: 'xs' | 'sm' | 'md';
}

const COLOR: Record<string, string> = {
  production: 'green',
  staging: 'orange',
  development: 'blue',
  preview: 'violet',
  local: 'gray'
};

const LABEL: Record<string, string> = {
  production: 'Production',
  staging: 'Staging',
  development: 'Development',
  preview: 'Preview',
  local: 'Local'
};

/**
 * Environment indicator shown in admin headers; makes it obvious whether
 * you're operating on prod vs staging. `env` accepts any string; built-in
 * names get a curated color, unknown values fall back to gray.
 */
export const EnvironmentBadge = forwardRef(function EnvironmentBadge(
  { env, children, size = 'xs', ...rest }: EnvironmentBadgeProps,
  ref: Ref<HTMLSpanElement>
): ReactNode {
  const color = COLOR[env] ?? 'gray';
  const label = children ?? LABEL[env] ?? env;
  return (
    <Badge ref={ref} size={size} color={color} variant="light" {...rest}>
      {label}
    </Badge>
  );
});
